/**
 * resolveBrandLockup — decides how the sidebar brand cluster (logo/mark +
 * wordmark, and on the admin surface a tenant chip of name + slug) shows the
 * firm's name so it appears ONCE, never stacked 2–3× and clipped.
 *
 * firm-walkthrough 2026-07-20 (P2): a white-labelled firm's name rendered three
 * times in the top-left — inside the uploaded logo, again as a mid-word-clipped
 * wordmark beside it ("Meridian Fiduciar…"), and a third time as the tenant
 * chip's name — reading as broken clutter on a realistic ~30-char firm name.
 *
 * Rule: a LOGO already carries the firm's identity, so the text wordmark shows
 * only when there is NO logo; and the admin tenant-chip name is suppressed only
 * when it would exactly repeat a wordmark that is also showing. So the name is
 * present exactly once in the cluster in every combination, and the elements
 * that DO render text are free to wrap instead of clip.
 *
 * PURE — exported for unit tests; the layouts are thin glue over it.
 */

export interface BrandLockup {
  /**
   * Render the text wordmark beside the mark/logo? True only when there is no
   * logo (the logo is the identity when present).
   */
  showWordmark: boolean;
  /** The wordmark text — the firm name, or the baked `elreign` default. */
  wordmark: string;
  /**
   * (Admin sidebar) render the tenant-chip's firm-name line? Suppressed only
   * when it would exactly repeat a wordmark that is also showing — so the name
   * shows once. The slug line is unaffected (it is the unique tenant id).
   */
  showTenantName: boolean;
}

export function resolveBrandLockup(opts: {
  /** Does the active branding carry a logo image? */
  hasLogo: boolean;
  /** The active branding firm name (may be null/blank when unbranded). */
  brandingFirmName?: string | null;
  /** The richer resolved tenant name shown in the admin tenant-chip. Omit on
   *  surfaces without a chip (e.g. the client portal sidebar). */
  tenantName?: string | null;
}): BrandLockup {
  const trimmedFirm = (opts.brandingFirmName ?? '').trim();
  const wordmark = trimmedFirm || 'elreign';
  const showWordmark = !opts.hasLogo;
  const tenantName = (opts.tenantName ?? '').trim();
  // The chip name is redundant only when the wordmark is ALSO showing (no logo)
  // and the two strings are identical. With a logo, the chip is the sole textual
  // name and always shows; an 'elreign' wordmark beside a real tenant display
  // name is not a duplicate, so both show.
  const showTenantName = !(showWordmark && tenantName !== '' && tenantName === wordmark);
  return { showWordmark, wordmark, showTenantName };
}
